/**
 * WebSocket hook for messaging
 * Handles connection, reconnection, and message routing
 */
'use client';

import { useCallback, useRef, useEffect, useState } from 'react';
import { networkState } from '@/lib/networkState';
import { wsClient } from '@/lib/websocket';
import type { 
  ClientThread, 
  ThreadMessage, 
  EligibleMember,
  IncomingCall,
  TypingUser,
  ConnectionStatus
} from '../types';

export interface WebSocketCallbacks {
  // Thread events
  onThreadsList?: (threads: ClientThread[], crossClient: boolean, totalClients: number) => void;
  onThreadCreated?: (thread: ClientThread) => void;
  onThreadUpdated?: (thread: ClientThread) => void;
  onThreadDeleted?: (threadId: string) => void;
  onAddedToThread?: (threadId: string) => void;
  onRemovedFromThread?: (threadId: string) => void;
  onSearchResults?: (threads: ClientThread[], query: string) => void;
  
  // Message events
  onThreadMessages?: (threadId: string, messages: ThreadMessage[]) => void;
  onNewMessage?: (threadId: string, message: ThreadMessage) => void;
  onMessageSent?: (threadId: string, message: ThreadMessage) => void;
  onMessageDeleted?: (threadId: string, messageId: string) => void;
  onReactionUpdated?: (threadId: string, messageId: string, reactions: any[]) => void;
  onReadReceipt?: (threadId: string, userId: number, userName: string, readAt: string) => void;
  
  // Member events
  onEligibleMembers?: (members: EligibleMember[]) => void;
  onMemberAdded?: (threadId: string, member: any) => void;
  onMemberRemoved?: (threadId: string, userId: number) => void;
  
  // Badge/notification events
  onBadgeCountsUpdated?: (threadId: string, unreadCount: number, lastMessage?: ThreadMessage | null) => void;
  
  // Call events
  onIncomingCall?: (call: IncomingCall) => void;
  onCallAnswered?: (callId: string, answererId: number, answererName: string) => void;
  onCallDeclined?: (callId: string, declinerId: number) => void;
  onCallEnded?: (callId: string, duration?: number) => void;
  
  // Typing events
  onUserTyping?: (threadId: string, user: TypingUser) => void;
  onUserStoppedTyping?: (threadId: string, userId: number) => void;
  
  // Connection events
  onConnected?: () => void;
  onDisconnected?: () => void;
  onError?: (error: string) => void;
}

interface UseMessagesWebSocketOptions {
  enabled?: boolean;
  userId?: number;
  isAuthenticated?: boolean;
  isOpen?: boolean;
  callbacks?: WebSocketCallbacks;
}

export function useMessagesWebSocket({
  enabled = true,
  userId,
  isAuthenticated,
  isOpen = true,
  callbacks = {}
}: UseMessagesWebSocketOptions) {
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>('disconnected');
  const callbacksRef = useRef(callbacks);
  const mountedRef = useRef(true); // Track if component is mounted
  const connectionStatusRef = useRef<ConnectionStatus>('disconnected');
  const manuallyPausedRef = useRef(false);
  
  // Keep callbacks ref updated
  useEffect(() => {
    callbacksRef.current = callbacks;
  }, [callbacks]);

  const updateConnectionStatus = useCallback((nextStatus: ConnectionStatus, emitCallbacks = true) => {
    const previousStatus = connectionStatusRef.current;
    if (previousStatus === nextStatus) {
      return;
    }

    connectionStatusRef.current = nextStatus;
    setConnectionStatus(nextStatus);

    if (!emitCallbacks) {
      return;
    }

    if (nextStatus === 'connected') {
      callbacksRef.current.onConnected?.();
    } else if (nextStatus === 'disconnected' && previousStatus !== 'disconnected') {
      callbacksRef.current.onDisconnected?.();
    }
  }, []);

  const syncConnectionStatusFromClient = useCallback((emitCallbacks = true) => {
    const sharedStatus = wsClient.getStatus();
    const nextStatus: ConnectionStatus = sharedStatus === 'connected'
      ? 'connected'
      : sharedStatus === 'reconnecting'
        ? 'reconnecting'
        : sharedStatus === 'connecting'
          ? 'connecting'
          : 'disconnected';

    updateConnectionStatus(nextStatus, emitCallbacks);
  }, [updateConnectionStatus]);

  // Handle incoming WebSocket messages
  const handleMessage = useCallback((data: any) => {
    const cb = callbacksRef.current;
    
    switch (data.type) {
      case 'client_threads_list':
        console.log('[WS Hook] Received client_threads_list:', data.threads?.length || 0, 'threads');
        cb.onThreadsList?.(
          data.threads || [], 
          data.cross_client ?? true,
          data.total_clients ?? 1
        );
        break;

      case 'client_threads_search_results':
        cb.onSearchResults?.(data.threads || [], data.query || '');
        break;

      case 'client_thread_created':
        cb.onThreadCreated?.(data.thread);
        break;

      case 'thread_messages':
        cb.onThreadMessages?.(data.thread_id, data.messages || []);
        break;

      case 'new_client_thread_message':
      case 'new_message': {
        const threadId = data.thread_id || data.message?.thread_id;
        if (threadId && data.message) {
          cb.onNewMessage?.(threadId, data.message);
        }
        break;
      }

      case 'message_sent':
        if (data.message) {
          cb.onMessageSent?.(data.thread_id, data.message);
        }
        break;

      case 'message_deleted':
        if (data.thread_id && data.message_id) {
          cb.onMessageDeleted?.(data.thread_id, data.message_id);
        }
        break;

      case 'badge_counts_updated':
        if (data.data?.thread_id) {
          cb.onBadgeCountsUpdated?.(
            data.data.thread_id, 
            data.data.unread_count ?? 0,
            data.data.last_message ?? null
          );
        }
        break;

      case 'eligible_members':
        cb.onEligibleMembers?.(data.members || []);
        break;

      case 'member_added':
        cb.onMemberAdded?.(data.thread_id, data.member);
        break;

      case 'member_removed':
      case 'member_left':
        cb.onMemberRemoved?.(data.thread_id, data.user_id);
        break;

      case 'added_to_thread':
        cb.onAddedToThread?.(data.thread_id);
        break;

      case 'removed_from_thread':
        cb.onRemovedFromThread?.(data.thread_id);
        break;

      case 'client_thread_left':
        // User successfully left the thread
        cb.onThreadDeleted?.(data.thread_id);
        break;

      case 'client_thread_deleted':
      case 'thread_deleted':
        // Thread was deleted (by user or another admin)
        cb.onThreadDeleted?.(data.thread_id);
        break;

      case 'thread_archived':
        // Thread was archived via the messages UI or industry-solution deactivation
        cb.onThreadDeleted?.(data.thread_id);
        break;

      case 'thread_updated':
        cb.onThreadUpdated?.(data.thread);
        break;

      case 'message_reaction_updated':
        cb.onReactionUpdated?.(data.thread_id, data.message_id, data.reactions);
        break;

      case 'messages_read_receipt':
        cb.onReadReceipt?.(
          data.thread_id, 
          data.user_id, 
          data.user_name,
          data.read_at
        );
        break;

      // Voice/Video call signaling
      case 'incoming_call':
        cb.onIncomingCall?.({
          callId: data.call_id,
          callerId: data.caller_id,
          callerName: data.caller_name,
          callerAvatar: data.caller_avatar,
          threadId: data.thread_id,
          threadName: data.thread_name,
          callType: data.call_type || 'voice',
          timestamp: data.timestamp
        });
        break;

      case 'call_answered':
        cb.onCallAnswered?.(data.call_id, data.answerer_id, data.answerer_name);
        break;

      case 'call_declined':
        cb.onCallDeclined?.(data.call_id, data.decliner_id);
        break;

      case 'call_ended':
        cb.onCallEnded?.(data.call_id, data.duration);
        break;

      // Typing indicators
      case 'user_typing':
      case 'user_activity':
        cb.onUserTyping?.(data.thread_id, {
          userId: data.user_id,
          userName: data.user_name,
          timestamp: Date.now()
        });
        break;

      case 'user_stopped_typing':
      case 'user_stopped_activity':
        cb.onUserStoppedTyping?.(data.thread_id, data.user_id);
        break;

      case 'error': {
        // Handle both legacy format { type: 'error', message: '...' } 
        // and channel format { type: 'error', data: { message: '...' } }
        const errorMsg = data.message || data.data?.message || data.error || '';
        if (errorMsg) {
          // Suppress non-actionable errors (channel protocol race conditions)
          const suppressedErrors = ['not authenticated', 'not authorized', 'subscription rate'];
          const isSuppressed = suppressedErrors.some(s => errorMsg.toLowerCase().includes(s));
          if (isSuppressed) {
            console.debug('[WS Hook] Suppressed WS error:', errorMsg);
          } else {
            console.error('[WS Hook] WebSocket error:', errorMsg);
            cb.onError?.(errorMsg);
          }
        }
        break;
      }
    }
  }, []);

  // Connect to WebSocket
  const connect = useCallback(async () => {
    if (!isAuthenticated || !userId || !enabled) {
      console.log('[WS Hook] Connection skipped - not ready:', { isAuthenticated, userId: !!userId, enabled });
      return;
    }
    
    // Check network state before attempting connection
    if (!networkState.isOnline()) {
      console.log('[WS Hook] Network offline, deferring connection');
      return;
    }

    manuallyPausedRef.current = false;

    if (wsClient.isConnected() || wsClient.getStatus() === 'connected') {
      syncConnectionStatusFromClient();
      return;
    }

    syncConnectionStatusFromClient(false);

    try {
      await wsClient.connect();
      syncConnectionStatusFromClient();
    } catch (err) {
      console.error('[WS Hook] Failed to connect shared WebSocket:', err);
      updateConnectionStatus('disconnected', false);
    }
  }, [enabled, isAuthenticated, syncConnectionStatusFromClient, updateConnectionStatus, userId]);

  // Disconnect this hook instance from realtime updates without tearing down
  // the shared singleton transport. ClientLayout owns the global socket
  // lifecycle, and feature-level cleanup must not close it for the rest of
  // the app while authentication or subscriptions are still in flight.
  const disconnect = useCallback(() => {
    manuallyPausedRef.current = true;
    updateConnectionStatus('disconnected', false);
  }, [updateConnectionStatus]);

  // Send a message through WebSocket
  const send = useCallback((message: Record<string, any>) => {
    if (!enabled || !isOpen || !isAuthenticated || !userId || manuallyPausedRef.current) {
      return false;
    }

    return wsClient.send(message);
  }, [enabled, isAuthenticated, isOpen, userId]);

  // Convenience methods for common operations
  const getThreads = useCallback(() => {
    console.log('[WS Hook] getThreads() called, status:', wsClient.getStatus());
    return send({ type: 'get_client_threads' });
  }, [send]);

  const joinThread = useCallback((threadId: string) => {
    return send({ type: 'join_thread', thread_id: threadId });
  }, [send]);

  const leaveThread = useCallback((threadId: string) => {
    return send({ type: 'leave_thread', thread_id: threadId });
  }, [send]);

  const getThreadMessages = useCallback((threadId: string) => {
    return send({ type: 'get_thread_messages', thread_id: threadId });
  }, [send]);

  const sendMessage = useCallback((
    threadId: string, 
    content: string, 
    options?: {
      attachments?: any[];
      replyTo?: { message_id: string; content: string; sender_name: string };
      mentions?: number[];
      messageType?: string;
      tempId?: string;
      location?: { latitude: number; longitude: number; accuracy?: number; address?: string };
    }
  ) => {
    return send({
      type: 'send_client_thread_message',
      thread_id: threadId,
      content,
      message_type: options?.messageType || (options?.attachments?.length ? 'attachment' : 'text'),
      attachments: options?.attachments || [],
      reply_to: options?.replyTo,
      mentions: options?.mentions,
      temp_id: options?.tempId,
      location: options?.location
    });
  }, [send]);

  const createThread = useCallback((
    name: string, 
    description?: string, 
    memberIds?: number[]
  ) => {
    return send({
      type: 'create_client_thread',
      thread_name: name,
      thread_description: description,
      initial_member_ids: memberIds
    });
  }, [send]);

  const addMember = useCallback((threadId: string, userId: number) => {
    return send({
      type: 'add_thread_member',
      thread_id: threadId,
      user_id: userId
    });
  }, [send]);

  const removeMember = useCallback((threadId: string, userId: number) => {
    return send({
      type: 'remove_thread_member',
      thread_id: threadId,
      user_id: userId
    });
  }, [send]);

  const addReaction = useCallback((threadId: string, messageId: string, emoji: string) => {
    return send({
      type: 'add_message_reaction',
      thread_id: threadId,
      message_id: messageId,
      emoji
    });
  }, [send]);

  const sendTyping = useCallback((threadId: string) => {
    return send({
      type: 'user_typing',
      thread_id: threadId
    });
  }, [send]);

  const sendStoppedTyping = useCallback((threadId: string) => {
    return send({
      type: 'user_stopped_typing',
      thread_id: threadId
    });
  }, [send]);

  const markAsRead = useCallback((threadId: string) => {
    return send({
      type: 'mark_thread_read',
      thread_id: threadId
    });
  }, [send]);

  const searchThreads = useCallback((query: string) => {
    return send({
      type: 'search_client_threads',
      query
    });
  }, [send]);

  const getEligibleMembers = useCallback(() => {
    return send({
      type: 'get_eligible_members'
    });
  }, [send]);

  const updateViewingStatus = useCallback((isViewing: boolean, threadId: string | null) => {
    return send({
      type: 'user_viewing_status',
      is_viewing_messages: isViewing,
      current_thread_id: threadId
    });
  }, [send]);

  // Call operations
  const initiateCall = useCallback((threadId: string, callType: 'voice' | 'video') => {
    return send({
      type: 'initiate_call',
      thread_id: threadId,
      call_type: callType
    });
  }, [send]);

  const answerCall = useCallback((callId: string) => {
    return send({
      type: 'answer_call',
      call_id: callId
    });
  }, [send]);

  const declineCall = useCallback((callId: string) => {
    return send({
      type: 'decline_call',
      call_id: callId
    });
  }, [send]);

  const endCall = useCallback((callId: string) => {
    return send({
      type: 'end_call',
      call_id: callId
    });
  }, [send]);

  useEffect(() => {
    const isActive = enabled && isAuthenticated && !!userId && isOpen;

    const unsubscribeConnection = wsClient.onConnection((status) => {
      if (!mountedRef.current) {
        return;
      }

      if (!isActive || manuallyPausedRef.current) {
        updateConnectionStatus('disconnected', false);
        return;
      }

      const nextStatus: ConnectionStatus = status === 'connected'
        ? 'connected'
        : status === 'reconnecting'
          ? 'reconnecting'
          : status === 'connecting'
            ? 'connecting'
            : 'disconnected';

      updateConnectionStatus(nextStatus);

      if (nextStatus === 'connected' && userId) {
        const joined = wsClient.send({ type: 'join_thread', thread_id: `system_${userId}` });
        if (joined) {
          console.log('[WS Hook] 📤 Sent join_thread for system_' + userId);
        }
      }
    });

    const unsubscribeLegacy = wsClient.onLegacyMessage((data) => {
      if (!mountedRef.current || !isActive || manuallyPausedRef.current) {
        return;
      }

      handleMessage(data);
    });

    return () => {
      unsubscribeConnection();
      unsubscribeLegacy();
    };
  }, [enabled, handleMessage, isAuthenticated, isOpen, updateConnectionStatus, userId]);

  // Effect to activate/pause this hook based on dependencies. The shared
  // WebSocket transport is bootstrapped globally in ClientLayout, so cleanup
  // here must only detach this hook's local participation.
  useEffect(() => {
    mountedRef.current = true;
    
    console.log('[WS Hook] Connection effect triggered:', { enabled, isAuthenticated, userId, isOpen });
    
    // Longer delay to handle React Strict Mode double invocation
    // React Strict Mode: mount -> unmount -> mount
    // The 200ms delay ensures we only connect on the second (final) mount
    // This prevents the "WebSocket closed during thread fetch" issue
    let connectTimeout: NodeJS.Timeout | null = null;
    
    if (enabled && isAuthenticated && userId && isOpen) {
      connectTimeout = setTimeout(() => {
        if (mountedRef.current) {
          void connect();
        }
      }, 200); // Increased delay to properly debounce React Strict Mode remounts
    }
    
    return () => {
      mountedRef.current = false;
      if (connectTimeout) {
        clearTimeout(connectTimeout);
      }
      disconnect();
    };
  }, [enabled, isAuthenticated, userId, isOpen, connect, disconnect]);

  return {
    // State
    isConnected: connectionStatus === 'connected',
    connectionStatus,
    
    // Raw operations
    send,
    connect,
    disconnect,
    
    // Thread operations
    getThreads,
    joinThread,
    leaveThread,
    searchThreads,
    createThread,
    getEligibleMembers,
    
    // Message operations
    getThreadMessages,
    sendMessage,
    addReaction,
    markAsRead,
    
    // Member operations
    addMember,
    removeMember,
    
    // Typing operations
    sendTyping,
    sendStoppedTyping,
    
    // Call operations
    initiateCall,
    answerCall,
    declineCall,
    endCall,
    
    // Status operations
    updateViewingStatus
  };
}

export default useMessagesWebSocket;
